import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { IconZoomIn } from 'assets/icons';

import { BookingConnection, BookingEdge, BookingState } from 'types/api';
import { callWebviewHandler } from 'utils/webview';
import { WebviewOperatorType } from 'utils/webview';
import { isWebview } from 'utils/webview';
import { getEnvConstant } from 'constants/env';

import BookingUserCardV2 from '../components/BookingUserCard.v2';
import CheckBox from 'components/common/CheckBox';
import Row from 'components/common/Row';
import { SALE_DETAIL_PATH, SALE_LIST_PATH } from 'constants/path';
import EmptyList from '../components/EmptyList';
import SearchInput from 'components/SearchInput/SearchInput';
import MultipleBookingUserCardV2 from '../components/MultipleBookingUserCard.v2';
import { useIsMobile } from 'hooks/common';

const InputContainer = styled.div`
  width: 100%;

  padding: 0 20px;
  margin-bottom: 24px;
`;

const UserListBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  height: auto;

  padding: 0 20px 64px;

  .overview-text {
    margin-bottom: 4px;

    font-weight: 500;
    font-size: 15px;
    line-height: 22px;

    color: #46474c;

    &.colored {
      color: rgba(0, 113, 233, 1);
    }
  }
`;

const CheckAttendanceBox = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  margin-left: auto;

  > p {
    margin: 0;

    color: #989ba2;
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
  }
`;

interface Props {
  userList: BookingEdge[];
  searchedKeyword: string;
  searchKeyword: string;
  keywordHandler: (value: string) => void;
  onSearch: () => void;
  needToCheckAttendance?: boolean;
  checkAllUserHandler: () => void;
  checkUserHandler: (ticketIdList: string[], bookingId: string) => void;
  checkedList: Record<string, string[]>;
  onClickCancelAbsent: (checkedTicketIdList: string[]) => void;
  onClickCancelAttendance: (checkedTicketIdList: string[]) => void;
  timeline: {
    confirmedAt: string;
    usedAt: string;
    absentAt: string;
  };
  ticketType: 'fixed' | 'unfixed';
  startTime: number;
  handleCancelBookingConfirmModal: () => void;
  saveSelectedConfirmedBookingIds: (
    selectedConfirmedBookingIds: string[],
  ) => void;
}

function UserListContent({
  userList,
  searchedKeyword,
  searchKeyword,
  keywordHandler,
  onSearch,
  needToCheckAttendance = false,
  checkAllUserHandler,
  checkUserHandler,
  checkedList,
  onClickCancelAbsent,
  onClickCancelAttendance,
  timeline,
  ticketType,
  startTime,
  handleCancelBookingConfirmModal,
  saveSelectedConfirmedBookingIds,
}: Props) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const sumUpTotalCount = userList.reduce((acc, { node: cur }) => {
    const isCanceled = cur.status === BookingState.CANCELED;

    if (isCanceled) {
      return acc;
    }

    const realTicketList = cur.items.filter(item =>
      item.tickets.some(
        ticket => ticket?.scheduleTerm?.startedAt === startTime,
      ),
    );

    return realTicketList.reduce((acc, cur2) => {
      if (cur2.tickets.length > 1) {
        const realTicketList = cur2.tickets.filter(ticket => {
          return ticket?.scheduleTerm?.startedAt === startTime;
        });

        return acc + realTicketList.length;
      }

      return (
        acc +
        cur2.counts.applied +
        cur2.counts.confirmed +
        cur2.counts.used +
        cur2.counts.absent
      );
    }, acc);
  }, 0);

  const isAllCheckedAttendance = userList.every(
    ({ node: data }) =>
      data.status === BookingState.USED || data.status === BookingState.ABSENT,
  );

  const attendedCount = userList.reduce((acc, { node: data }) => {
    const { applied, confirmed, used, absent } = data.counts;

    return acc + used;
  }, 0);

  const absentedCount = userList.reduce((acc, { node: data }) => {
    const { applied, confirmed, used, absent } = data.counts;

    return acc + absent;
  }, 0);

  const isAllChecked =
    Object.keys(checkedList).length ===
      userList.filter(
        ({ node: data }) =>
          ![BookingState.USED, BookingState.ABSENT].includes(data.status),
      ).length && Object.keys(checkedList).length !== 0;

  const handleShowUserCardDetail = (itemId: string) => {
    setSelectedItemId(pre => {
      if (itemId === pre) {
        return null;
      }

      return itemId;
    });
  };

  const goToSalesPage = (orderId: string) => {
    if (isMobile) {
      navigate(`${SALE_DETAIL_PATH}/${orderId}`);
    } else {
      localStorage.setItem('orderId', orderId);
      navigate(SALE_LIST_PATH);
    }
  };

  const goToMessagePage = (orderId: string) => {
    if (isWebview()) {
      // item.bookingItem.booking.order.id
      callWebviewHandler(WebviewOperatorType.OpenActionUrl, {
        actionUrl: `frip://chattingDetail/O${orderId}`,
      });

      return;
    }

    window.open(`${getEnvConstant('webClientUrl')}/messageDetail/O${orderId}`);
  };

  return (
    <>
      <InputContainer>
        <SearchInput
          searchKeyword={searchKeyword}
          keywordHandler={keywordHandler}
          handleSearch={onSearch}
          placeholder="크루명이나 번호를 입력해 주세요."
          isSearched={searchKeyword !== '' && searchKeyword === searchedKeyword}
        />
      </InputContainer>

      <UserListBox>
        <Row justifyContent="space-between">
          <p
            className={
              searchedKeyword ? 'overview-text colored' : 'overview-text'
            }
          >
            {searchedKeyword ? (
              `검색적용: ${sumUpTotalCount}건 `
            ) : isAllCheckedAttendance ? (
              <>
                참석: {attendedCount}건<span>{'\u2502'}</span>
                불참: {absentedCount}건
              </>
            ) : (
              sumUpTotalCount > 1 && `총 예약확정: ${sumUpTotalCount}건`
            )}
          </p>

          {userList.length > 1 &&
            (isAllCheckedAttendance ? (
              <></>
            ) : (
              <>
                <CheckBox
                  isChecked={isAllChecked}
                  onChange={checkAllUserHandler}
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '2px',
                    borderColor: isAllChecked ? '#000' : '#989BA2',
                  }}
                  label="전체선택"
                />
              </>
            ))}
        </Row>

        {userList.length === 0 ? (
          <EmptyList />
        ) : (
          userList.map(({ node: data }, idx) => {
            const isLast = userList.length - 1 === idx;
            const isSelectedUser = data.id === selectedItemId;

            const isCanceled = data.status === BookingState.CANCELED;

            const isMultipleTicket = data.items[0].tickets.length > 1;

            return (
              !isCanceled &&
              (isMultipleTicket ? (
                <MultipleBookingUserCardV2
                  key={`booking-user-list-${idx}-${data.id}`}
                  data={data}
                  isSelectedUser={isSelectedUser}
                  handleShowUserCardDetail={handleShowUserCardDetail}
                  idx={idx}
                  selectedItemId={selectedItemId}
                  goToSalesPage={goToSalesPage}
                  goToMessagePage={goToMessagePage}
                  needToCheckAttendance={needToCheckAttendance}
                  checkUserHandler={checkUserHandler}
                  checkedList={checkedList}
                  onClickCancelAbsent={onClickCancelAbsent}
                  onClickCancelAttendance={onClickCancelAttendance}
                  timeline={timeline}
                  ticketType={ticketType}
                  startTime={startTime}
                  handleCancelBookingConfirmModal={
                    handleCancelBookingConfirmModal
                  }
                  saveSelectedConfirmedBookingIds={
                    saveSelectedConfirmedBookingIds
                  }
                />
              ) : (
                <BookingUserCardV2
                  key={`booking-user-list-${idx}-${data.id}`}
                  data={data}
                  isSelectedUser={isSelectedUser}
                  handleShowUserCardDetail={handleShowUserCardDetail}
                  idx={idx}
                  selectedItemId={selectedItemId}
                  goToSalesPage={goToSalesPage}
                  goToMessagePage={goToMessagePage}
                  needToCheckAttendance={needToCheckAttendance}
                  checkUserHandler={checkUserHandler}
                  checkedList={checkedList}
                  onClickCancelAbsent={onClickCancelAbsent}
                  onClickCancelAttendance={onClickCancelAttendance}
                  timeline={timeline}
                  ticketType={ticketType}
                  handleCancelBookingConfirmModal={
                    handleCancelBookingConfirmModal
                  }
                  startTime={startTime}
                  saveSelectedConfirmedBookingIds={
                    saveSelectedConfirmedBookingIds
                  }
                />
              ))
            );
          })
        )}
      </UserListBox>
    </>
  );
}

export default UserListContent;
